import { useCallback, useState } from "react";

type FileData = string | Blob;
/**
 * Hook that returns a function to download a file with the contents specified.
 */
export const useDownloadFile = (params: {
  data: FileData | (() => Promise<FileData>);
  filename: string;
  type: `${string}/${string}`;
  description?: string;
  onSuccess?: () => void;
  onError?: (err: unknown) => void;
}) => {
  const [pending, setPending] = useState(false);
  const execute = useCallback(async () => {
    if (!params.data) return;

    setPending(true);
    const data =
      typeof params.data === "function" ? await params.data() : params.data;
    const blob =
      data instanceof Blob ? data : new Blob([data], { type: params.type });

    if ("showSaveFilePicker" in window) {
      try {
        const ext = params.filename.split(".")[1];
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: params.filename,
          types: [
            {
              description: params.description,
              accept: { [params.type]: `.${ext}` },
            },
          ],
        });

        const fileStream = await fileHandle.createWritable();
        await fileStream.write(blob);
        await fileStream.close();
        params.onSuccess?.();
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        params.onError?.(error);
      } finally {
        setPending(false);
      }
      return;
    }

    /** For legacy browsers */
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = params.filename;
    a.click();

    params.onSuccess?.();
    setPending(false);
  }, [params]);

  return { execute, pending };
};
